import clsx from "clsx";
import { ReactElement, ReactNode, useEffect, useRef, useState } from "react";
import { useAppDispatch } from "../../../lib/redux/hooks";
import { SpinnerComponent } from "../../../lib/spinner";
import { GetCustomerByCompanyNameResponseDTO, getCustomersByIdResponseDTO } from "../../../pages/clienti/dto";
import { GetCustomersByCompanyName, GetCustomersById } from "../../../pages/clienti/slice";
import { formatterDate } from "../../../utils";
import { RoundedCloseIcon } from "../../icons/CloseIcon";

type DefaultCustomerItem = GetCustomerByCompanyNameResponseDTO | getCustomersByIdResponseDTO;

interface InputMultiSearchProps<T> {
    onSearch?: (query: string) => Promise<T[]>
    onFetchById?: (id: string) => Promise<T>
    getId?: (item: T) => string

    renderResult?: (item: T, isSelected: boolean) => ReactNode
    renderSelected?: (item: T) => ReactNode

    defaultIds?: string[]
    defaultClientIds?: string[]

    label?: string
    placeholder?: string
    supportingText?: string
    startIcon?: ReactElement
    endIcon?: ReactElement
    error?: boolean
    disabled?: boolean
    required?: boolean
    searchOne?: boolean

    onSelect?: (id: string) => void
    onSelectAddHandler?: (ids: string[]) => void
    onChangeText?: (text: string) => void
    onClick?: () => void
}

function InputMultiSearch<T>({ ...props }: InputMultiSearchProps<T>) {
    const dispatch = useAppDispatch();
    const [searchResults, setSearchResults] = useState<T[]>([]);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [selectedItems, setSelectedItems] = useState<T[]>([]);
    const [inputValue, setInputValue] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isFocused, setIsFocused] = useState<boolean>(false); // ← unica fonte di verità

    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isFirstMount = useRef(true);
    const showResults = isFocused && searchResults.length > 0;

    const getItemId = (item: T): string => {
        if (props.getId) return props.getId(item);
        return (item as unknown as { id: string }).id;
    };

    const searchItems = async (query: string): Promise<T[]> => {
        if (props.onSearch) return props.onSearch(query);
        const response = await dispatch(GetCustomersByCompanyName(query)).unwrap();
        return (response?.data ?? []) as T[];
    };

    const fetchItemById = async (id: string): Promise<T> => {
        if (props.onFetchById) return props.onFetchById(id);
        const response = await dispatch(GetCustomersById(id)).unwrap();
        return response as T;
    };

    const defaultRenderResult = (item: T, isSelected: boolean): ReactNode => {
        const customer = item as unknown as DefaultCustomerItem;
        const isInactive = customer.endingDate !== null;
        return (
            <div className="flex flex-col gap-0.5 items-start">
                <span className={clsx('font-semibold text-left', {
                    'text-red-600': isInactive && !isSelected,
                    'text-brandPrimary-700': !isInactive && !isSelected,
                })}>
                    {customer.companyName}
                </span>
                <span className="text-xs text-neutral-600 text-left">
                    {customer.countryRegion} - {customer.cuaa} - {customer.clientCode}
                </span>
                <span className="text-xs text-neutral-500 text-left">
                    Data In: {formatterDate(customer.startingDate)} | Data Out: {customer.endingDate ? formatterDate(customer.endingDate) : '-'}
                </span>
            </div>
        );
    };

    const defaultRenderSelected = (item: T): ReactNode => {
        const customer = item as unknown as DefaultCustomerItem;
        return (
            <div className="flex flex-col gap-0.5 flex-1 items-start">
                <span className="text-sm font-bold text-brandPrimary-700 text-left">{customer.companyName}</span>
                <span className="text-xs text-brandPrimary-600 text-left">
                    {customer.countryRegion} - {customer.cuaa} - {customer.clientCode}
                </span>
                <span className="text-xs text-brandPrimary-600 text-left">
                    Data In: {formatterDate(customer.startingDate)} | Data Out: {customer.endingDate ? formatterDate(customer.endingDate) : '-'}
                </span>
            </div>
        );
    };

    useEffect(() => {
        const ids = props.defaultIds ?? props.defaultClientIds;
        if (ids && ids.length > 0) {
            Promise.all(ids.map(id => fetchItemById(id)))
                .then(items => {
                    setSelectedIds(ids);
                    setSelectedItems(items);
                })
                .catch(console.error);
        }
    }, []);

    useEffect(() => {
        if (isFirstMount.current) {
            isFirstMount.current = false;
            return;
        }
        setSelectedIds([]);
        setSelectedItems([]);
        setSearchResults([]);
        setInputValue('');
        setIsFocused(false);
    }, [props.disabled]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setInputValue(value);
        props.onChangeText?.(value);

        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

        if (value.length > 2) {
            setIsLoading(true);
            searchTimeoutRef.current = setTimeout(async () => {
                try {
                    const results = await searchItems(value);
                    setSearchResults(results);
                } catch (error) {
                    console.error('Errore nella ricerca:', error);
                    setSearchResults([]);
                } finally {
                    setIsLoading(false);
                }
            }, 300);
        } else {
            setIsLoading(false);
            setSearchResults([]);
        }
    };

    const handleSelectResult = (item: T) => {
        const id = getItemId(item);
        if (selectedIds.includes(id)) return;

        const newIds = [...selectedIds, id];
        const newItems = [...selectedItems, item];

        setSelectedIds(newIds);
        setSelectedItems(newItems);
        setSearchResults([]);
        setIsFocused(false);
        setInputValue('');

        props.onSelectAddHandler?.(newIds);
        props.onSelect?.(id);
    };

    const handleOnDelete = (item: T) => {
        const id = getItemId(item);
        setSelectedIds(prev => prev.filter(i => i !== id));
        setSelectedItems(prev => prev.filter(i => getItemId(i) !== id));
        props.onSelectAddHandler?.(selectedIds.filter(i => i !== id));
    };

    const isDisabled = props.disabled || (props.searchOne && selectedItems.length > 0);

    return (
        <div className="flex flex-col gap-1 w-full items-start relative">
            {props.label && (
                <span style={{ fontFamily: "Manrope" }} className="text-label-md font-bold text-brandPrimary-800">
                    {props.label}
                    {props.required && <span className='text-red-600'> *</span>}
                </span>
            )}
            <div className={clsx(
                'flex box-shadow-xs items-center gap-2 border rounded-lg h-[48px] max-h-[48px] justify-start px-3.5 py-0 w-full',
                {
                    'bg-white border-neutral-300': !isDisabled && !props.error,
                    'bg-[rgba(242,242,250,1)] border-neutral-300': isDisabled,
                    'bg-white border-red-600': props.error && !isDisabled,
                },
            )}>
                {props.startIcon && <div className="flex-shrink-0">{props.startIcon}</div>}
                <input
                    ref={inputRef}
                    placeholder={props.placeholder}
                    disabled={isDisabled}
                    value={inputValue}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => {
                        setTimeout(() => setIsFocused(false), 150);
                    }}
                    onChange={handleInputChange}
                    onClick={props.onClick}
                    style={{ border: 0, outline: 0, height: '20px', width: '100%', fontFamily: "Manrope" }}
                    className={clsx(
                        'bg-transparent text-text-md text-left text-neutral-700 decoration-0 p-0 ring-0 focus:ring-0 font-medium disabled:text-neutral-500',
                        'placeholder:text-text-md placeholder:text-neutral-500 placeholder:font-medium',
                    )}
                />
                {isLoading && <SpinnerComponent size={'small'} />}
                {props.endIcon && <div className="flex-shrink-0">{props.endIcon}</div>}
            </div>

            {props.supportingText && (
                <span style={{ fontFamily: "Manrope" }} className={clsx('text-sm font-medium', {
                    'text-red-500': props.error,
                    'text-neutral-400': !props.error,
                })}>
                    {props.supportingText}
                </span>
            )}

            {showResults && (
                <div
                    ref={dropdownRef}
                    className="absolute top-full left-0 right-0 mt-1 z-[9999] max-h-64 overflow-y-auto bg-white border border-neutral-300 rounded-lg shadow-lg"
                >
                    <ul className="py-1">
                        {searchResults.map((item) => {
                            const id = getItemId(item);
                            const isSelected = selectedIds.includes(id);
                            return (
                                <li
                                    key={id}
                                    onMouseDown={(e) => {
                                        e.preventDefault(); 
                                        if (!isSelected) handleSelectResult(item);
                                    }}
                                    style={{ fontFamily: "Manrope" }}
                                    className={clsx(
                                        'px-4 py-3 text-sm font-medium transition-colors text-left',
                                        {
                                            'cursor-pointer hover:bg-neutral-100': !isSelected,
                                            'bg-neutral-200 cursor-not-allowed opacity-60': isSelected,
                                        }
                                    )}
                                >
                                    {(props.renderResult ?? defaultRenderResult)(item, isSelected)}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}

            {selectedItems.length > 0 && (
                <div className="w-full mt-2 space-y-2">
                    {selectedItems.map((item) => {
                        const id = getItemId(item);
                        return (
                            <div
                                key={id}
                                className="flex items-center justify-between p-3 bg-brandPrimary-100 border border-brandPrimary-200 rounded-lg group hover:bg-brandPrimary-200 transition-colors"
                            >
                                <div className="flex-1 items-start">
                                    {(props.renderSelected ?? defaultRenderSelected)(item)}
                                </div>
                                <button
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => handleOnDelete(item)}
                                    className="flex-shrink-0 p-1 rounded-full hover:bg-brandPrimary-300 transition-colors"
                                >
                                    <RoundedCloseIcon size={18} color="#4D797F" />
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default InputMultiSearch;